import React, { useCallback, MutableRefObject } from 'react';
import cx from 'classnames';
import { KeyedAnalyticsData, useAnalytics } from '@johnlewispartnership/wtr-website-analytics';
import { LinkAsButton } from '@johnlewispartnership/wtr-ingredients/ingredients/LinkAsButton/index.js';
import Typography from '@johnlewispartnership/wtr-ingredients/foundations/typography/index.js';
import { Badge } from '@johnlewispartnership/wtr-ingredients/ingredients/Badge/index.js';
import { BadgeType } from '@johnlewispartnership/wtr-ingredients/ingredients/Badge/types.js';
import { urls } from 'constants/urls';
import { usePodObserver } from 'hooks/use-pod-observer';
import { type Banner } from 'services/banners/types';
import styles from './AdtechCard.module.scss';

interface AdtechAnalyticsEvent extends KeyedAnalyticsData {
  /**
   * Urls to be invoked on SGTM
   */
  trackingUrls: string[];
  /**
   * The suppliers' product sponsorshipId
   */
  sponsorshipId: string;
  /**
   * The type of tracking event invoked
   */
  event: 'impression' | 'click';
  /**
   *  Opaque value supplied by the backend
   */
  vendor: string;
}

type AdtechTracker = (event: AdtechAnalyticsEvent) => void;

const makeUrl = (products: Banner['products']) => {
  const lineNumbers = products.map(({ lineNumber }) => lineNumber).join('%2C');

  return `${urls.buylist}/${lineNumbers}`;
};

export const AdtechCard = ({
  banner: { id, textArea, image, products, marketingBadge, tracking },
  className,
}: { banner: Banner } & Pick<React.HTMLAttributes<HTMLDivElement>, 'className'>) => {
  const { trackEvent }: { trackEvent: AdtechTracker } = useAnalytics();

  const observerRef: MutableRefObject<HTMLDivElement> = usePodObserver(
    useCallback(() => {
      trackEvent({
        event: 'impression',
        sponsorshipId: id,
        trackingUrls: tracking.impressionUrls,
        vendor: tracking.vendor,
      });
    }, [id, tracking.impressionUrls, tracking.vendor, trackEvent]),
  ) as unknown as MutableRefObject<HTMLDivElement>;

  const callbackRef = useCallback(
    (node: HTMLDivElement) => {
      observerRef.current = node;
    },
    [observerRef],
  );

  return (
    <div ref={callbackRef} className={cx(styles.ad, className)}>
      <img src={image.src} alt="" />
      {marketingBadge && (
        <Badge className={styles.badge} type={BadgeType.GREEN} text={marketingBadge} />
      )}
      <h3>
        <span className={styles.promo}>{textArea.offerHeading}</span> {textArea.heading}
      </h3>
      <Typography>{textArea.subheading}</Typography>
      <LinkAsButton
        href={textArea.link.url || makeUrl(products)}
        width="full"
        aria-label={textArea.link.altText}
        target={textArea.link.isExternalLink ? '_blank' : undefined}
        rel={textArea.link.isExternalLink ? 'noopener noreferrer' : undefined}
        onClick={() => {
          trackEvent({
            event: 'click',
            sponsorshipId: id,
            trackingUrls: tracking.clickUrls,
            vendor: tracking.vendor,
          });
        }}
      >
        {textArea.link.text || 'Shop Now'}
      </LinkAsButton>
    </div>
  );
};
